import {
  Button,
  Flex,
  InputGroup,
  InputLeftAddon,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect } from 'react';
import InputSelector from '../../../../../components/shared/Inputs/InputSelector';
import InputText from '../../../../../components/shared/Inputs/InputText/index';
import InputNumber from '../../../../../components/shared/Inputs/InputNumber';
import { useAuth } from '../../../../../hooks/auth';
import { useValoresCertificadoUsuario } from '../../../../../services/hooks/useCustoCertificadoUsuario';
import { useSelecionaParceiroFilhoModal } from '../../../../../hooks/SelecionaParceiroFilhoModalContext';
import { usePermissoes } from '../../../../../services/hooks/usePermissoes';

export const FormSchema = z.object({
  tipo_documento: z.string().nonempty('Campo Obrigatório'),
  documento: z.string().nonempty('Campo Obrigatório'),
  nome: z.string().nonempty('Campo Obrigatório'),
  nome_fantasia: z.string().optional(),
  telefone: z.string().optional(),
  celular: z.string().nonempty('Campo Obrigatório'),
  email: z.string().email('Email Inválido').nonempty('Campo Obrigatório'),
  cep: z.string().nonempty('Campo Obrigatório'),
  logradouro: z.string().nonempty('Campo Obrigatório'),
  numero: z.string().nonempty('Campo Obrigatório'),
  bairro: z.string().nonempty('Campo Obrigatório'),
  uf: z.string().nonempty('Campo Obrigatório'),
  cidade: z.string().nonempty('Campo Obrigatório'),
  ibge: z.string().nonempty('Campo Obrigatório'),
  complemento: z.string().optional(),
  valor_comissao: z.number({ required_error: 'Obrigatório' }).min(0, 'A'),
  valor_venda: z.number({ required_error: 'Obrigatório' }).min(0, 'A'),
});

export type stepFaturaData = z.infer<typeof FormSchema>;

interface IDadosFatura {
  setFaturaData(data: any): void;
  handleMudaStep(novoStep: number): void;
  certificadoData: any;
}

const DadosFatura: React.FC<IDadosFatura> = ({
  setFaturaData,
  handleMudaStep,
  certificadoData,
}) => {
  const Toast = useToast();
  const color = useColorModeValue('gray.800', 'white');
  const details = useColorModeValue('gray.200', 'gray.700');

  const methods = useForm<stepFaturaData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      tipo_documento: certificadoData.tipo_documento,
      documento: certificadoData.documento,
      nome: certificadoData.razao_nome,
      nome_fantasia: certificadoData.fantasia,
      telefone: certificadoData.telefone,
      celular: certificadoData.celular,
      email: certificadoData.email,
      cep: certificadoData.cep,
      logradouro: certificadoData.logradouro,
      numero: certificadoData.numero,
      bairro: certificadoData.bairro,
      uf: certificadoData.uf,
      cidade: certificadoData.cidade,
      ibge: certificadoData.ibge,
      complemento: certificadoData.complemento,
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    register,
    setValue,
    control,
    formState: { errors },
  } = methods;

  // watchs
  const tipo_documento = watch('tipo_documento');
  const valor_comissao = watch('valor_comissao');

  const { user } = useAuth();
  const { permite, Permissoes } = usePermissoes();
  const { openModal, parceiro, setParceiro } = useSelecionaParceiroFilhoModal();

  useEffect(() => {
    setParceiro({
      id: user.id,
      nome: user.name,
    });
  }, []);

  const {
    data: valoresCertificadoUsuarioData,
    isLoading: isValorCertificadoLoading,
  } = useValoresCertificadoUsuario(parceiro?.id ?? user.id);

  useEffect(() => {
    setValue(
      'valor_comissao',
      valoresCertificadoUsuarioData?.valor_comissao ?? 0,
    );
    setValue('valor_venda', valoresCertificadoUsuarioData?.valor_venda ?? 0);
  }, [valoresCertificadoUsuarioData]);

  const handleSalvaDados = async (data: stepFaturaData) => {
    try {
      const dataFinal = {
        ...data,
        vendedor_id: parceiro?.id,
      };
      setFaturaData(dataFinal);
      handleMudaStep(4);
    } catch (error) {
      //
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(handleSalvaDados, errors => {
          Toast({
            title: 'Validação',
            description: 'Preencha os campos destacados.',
            status: 'error',
            duration: 4000,
            position: 'bottom-right',
            isClosable: true,
          });
        })}
      >
        <Flex justify="space-between" mx="8">
          <Button onClick={() => handleMudaStep(2)}>Voltar</Button>
          <Button type="submit" isDisabled={isValorCertificadoLoading}>
            Avançar
          </Button>
        </Flex>
        <Flex w="100%" p="8">
          <Flex flexDir="column" w="100%">
            <Flex flexDir="column">
              <Text color={color}>Vendedor</Text>
              <Flex
                borderWidth="1px"
                p="2"
                rounded="md"
                cursor={
                  permite(Permissoes.PERMISSAO_EDITAR_VENDEDOR_CERTIFICADO)
                    ? 'pointer'
                    : 'default'
                }
                w="300px"
                onClick={() => {
                  if (
                    permite(Permissoes.PERMISSAO_EDITAR_VENDEDOR_CERTIFICADO)
                  ) {
                    openModal();
                  }
                }}
              >
                <Text color={color}>{parceiro?.nome}</Text>
              </Flex>
            </Flex>
            <Flex mt="3" w="100%" flexDir="row" align="center">
              <SimpleGrid
                w="28%"
                opacity={isValorCertificadoLoading ? '0.5' : '1'}
                templateColumns={['1fr 1fr', '1fr 3fr', '1fr, 6fr']}
              >
                <InputGroup>
                  <InputLeftAddon
                    fontWeight="medium"
                    color={color}
                    roundedBottom="0px"
                  >
                    R$
                  </InputLeftAddon>
                  <Flex
                    w="150px"
                    align="center"
                    padding="7px"
                    borderWidth="1px"
                    borderTopRightRadius="xl"
                    bg="rgba(0,0,0,0.05)"
                    h="100%"
                  >
                    <Text color={color} pl="2">
                      {valoresCertificadoUsuarioData?.valor_custo ?? 0}
                    </Text>
                  </Flex>
                </InputGroup>
                <Text color={color} pl="2" alignSelf="center">
                  Custo certificado
                </Text>

                <Controller
                  control={control}
                  name="valor_comissao"
                  render={({ field }) => {
                    return (
                      <InputGroup>
                        <InputLeftAddon
                          fontWeight="medium"
                          color={color}
                          rounded="0px"
                        >
                          R$
                        </InputLeftAddon>
                        <InputNumber
                          color={color}
                          maskType="money"
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.valor_venda}
                          borderRadius="0px"
                        />
                      </InputGroup>
                    );
                  }}
                />
                <Text color={color} pl="2" alignSelf="center">
                  Comissão
                </Text>
                <InputGroup>
                  <InputLeftAddon roundedTop="0px">
                    <Text
                      bgGradient="linear(to-b, green.300, green.500)"
                      bgClip="text"
                      fontWeight="bold"
                    >
                      R$
                    </Text>
                  </InputLeftAddon>
                  <Flex
                    align="center"
                    padding="7px"
                    borderWidth="1px"
                    w="150px"
                    borderBottomRightRadius="xl"
                    bg="rgba(0,0,0,0.05)"
                  >
                    <Text
                      fontWeight="medium"
                      bgGradient="linear(to-b, green.300, green.500)"
                      bgClip="text"
                      pl="2"
                    >
                      {(valoresCertificadoUsuarioData?.valor_custo ?? 0) +
                        (valor_comissao ?? 0)}
                    </Text>
                  </Flex>
                </InputGroup>
                <Text color={color} pl="2" alignSelf="center">
                  Valor do certificado
                </Text>
              </SimpleGrid>
              {isValorCertificadoLoading && (
                <Flex>
                  <Spinner
                    emptyColor="gray.200"
                    color="blue.500"
                    thickness="4px"
                    size="xl"
                  />
                </Flex>
              )}
            </Flex>
            <SimpleGrid
              mt="1"
              templateColumns={['1fr', '1fr', '1fr 1fr 2fr 2fr']}
              gap={4}
              justify="center"
              w="100%"
            >
              <Controller
                control={control}
                name="tipo_documento"
                render={({ field }) => (
                  <InputSelector
                    {...field}
                    label="Tipo"
                    labelColor={color}
                    disableSelecione
                    options={[
                      {
                        label: 'FISICA',
                        value: '1',
                      },
                      {
                        label: 'JURIDICA',
                        value: '2',
                      },
                    ]}
                  />
                )}
              />
              {tipo_documento === '1' && (
                <Controller
                  control={control}
                  name="documento"
                  render={({ field }) => (
                    <InputText
                      label="CPF"
                      labelColor={color}
                      toUpperCase
                      mask="000.000.000-00"
                      {...field}
                      error={errors.documento}
                    />
                  )}
                />
              )}
              {tipo_documento === '2' && (
                <Controller
                  control={control}
                  name="documento"
                  render={({ field }) => (
                    <InputText
                      label="CNPJ"
                      labelColor={color}
                      toUpperCase
                      mask="**.***.***/****-00"
                      maskOptions={{
                        '*': /[A-Za-z0-9]/,
                      }}
                      {...field}
                      error={errors.documento}
                    />
                  )}
                />
              )}
              <Controller
                control={control}
                name="nome"
                render={({ field }) => (
                  <InputText
                    label="Nome/Razão"
                    labelColor={color}
                    {...field}
                    error={errors.nome}
                  />
                )}
              />
              <Controller
                control={control}
                name="nome_fantasia"
                render={({ field }) => (
                  <InputText
                    label="Sobrenome/Fantasia"
                    labelColor={color}
                    {...field}
                    error={errors.nome_fantasia}
                  />
                )}
              />
            </SimpleGrid>
            <SimpleGrid
              templateColumns={['1fr', '1fr', '1fr 1fr 2fr']}
              gap={4}
              justify="center"
              w="100%"
              mt="1"
            >
              <Controller
                control={control}
                name="telefone"
                render={({ field }) => (
                  <InputText
                    label="Telefone"
                    labelColor={color}
                    mask="(00) 00000-0000"
                    {...field}
                    error={errors.telefone}
                  />
                )}
              />
              <Controller
                control={control}
                name="celular"
                render={({ field }) => (
                  <InputText
                    label="Celular"
                    labelColor={color}
                    mask="(00) 00000-0000"
                    {...field}
                    error={errors.celular}
                  />
                )}
              />

              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <InputText
                    label="Email"
                    labelColor={color}
                    {...field}
                    error={errors.email}
                  />
                )}
              />
            </SimpleGrid>
            <Flex my="4" rounded="2xl" h="2px" w="100%" bg={details} />
            <SimpleGrid
              templateColumns={['1fr', '1fr', '1fr 2fr 1fr 2fr']}
              gap={4}
              justify="center"
              w="100%"
            >
              <Controller
                control={control}
                name="cep"
                render={({ field }) => (
                  <InputText
                    label="CEP"
                    labelColor={color}
                    {...field}
                    error={errors.cep}
                  />
                )}
              />
              <Controller
                control={control}
                name="logradouro"
                render={({ field }) => (
                  <InputText
                    label="Logradouro"
                    labelColor={color}
                    {...field}
                    error={errors.logradouro}
                  />
                )}
              />
              <Controller
                control={control}
                name="numero"
                render={({ field }) => (
                  <InputText
                    label="Nº"
                    labelColor={color}
                    {...field}
                    error={errors.numero}
                  />
                )}
              />
              <Controller
                control={control}
                name="bairro"
                render={({ field }) => (
                  <InputText
                    label="Bairro"
                    labelColor={color}
                    {...field}
                    error={errors.bairro}
                  />
                )}
              />
            </SimpleGrid>
            <SimpleGrid
              templateColumns={['1fr', '1fr', '1fr 1fr 1fr 2fr']}
              gap={4}
              justify="center"
              w="100%"
              mt="1"
            >
              <Controller
                control={control}
                name="uf"
                render={({ field }) => (
                  <InputText
                    label="UF"
                    labelColor={color}
                    {...field}
                    error={errors.uf}
                  />
                )}
              />
              <Controller
                control={control}
                name="cidade"
                render={({ field }) => (
                  <InputText
                    label="Cidade"
                    labelColor={color}
                    {...field}
                    error={errors.cidade}
                  />
                )}
              />
              <Controller
                control={control}
                name="ibge"
                render={({ field }) => (
                  <InputText
                    label="IBGE"
                    labelColor={color}
                    {...field}
                    error={errors.ibge}
                  />
                )}
              />
              <Controller
                control={control}
                name="complemento"
                render={({ field }) => (
                  <InputText
                    label="Complemento"
                    labelColor={color}
                    {...field}
                    error={errors.complemento}
                  />
                )}
              />
            </SimpleGrid>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default DadosFatura;
